<script setup lang="ts">
import { ref, watch, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import { ArrowDown } from '@element-plus/icons-vue'
interface TagView {
  path: string
  title: string
  name: string
}

const route = useRoute()
const router = useRouter()
const visitedViews = ref<TagView[]>([])

// 添加标签
const addView = (view: RouteLocationNormalized) => {
  if (!view.meta?.title) return
  const { path, meta, name } = view
  if (visitedViews.value.some((v) => v.path === path)) return

  visitedViews.value.push({
    path: path,
    title: meta.title as string,
    name: name as string,
  })
}

// 删除标签
const closeView = async (view: TagView) => {
  const index = visitedViews.value.findIndex((v) => v.path === view.path)
  if (index === -1) return

  visitedViews.value.splice(index, 1)

  if (view.path === route.path) {
    const latestView = visitedViews.value.slice(-1)[0]
    if (latestView) {
      router.push(latestView.path)
    } else {
      router.push('/')
    }
  }
}

// 关闭其他标签
const closeOthers = (view: TagView) => {
  visitedViews.value = visitedViews.value.filter((v) => v.path === view.path)
  if (route.path !== view.path) {
    router.push(view.path)
  }
}

// 关闭所有标签
const closeAll = () => {
  visitedViews.value = []
  router.push('/')
}

// 监听路由变化
watch(
  () => route.path,
  () => {
    addView(route)
  },
  { immediate: true },
)
</script>

<template>
  <div class="tags-view-container bg-white px-4 flex items-center border-b">
    <el-scrollbar class="tags-view-wrapper">
      <div class="tags-view-item flex items-center space-x-2">
        <el-tag
          v-for="tag in visitedViews"
          :key="tag.path"
          :closable="visitedViews.length > 1"
          :effect="route.path === tag.path ? 'dark' : 'plain'"
          class="mx-1 cursor-pointer"
          @click="router.push(tag.path)"
          @close.prevent="closeView(tag)"
        >
          <span>{{ tag.title }}</span>
        </el-tag>
      </div>
    </el-scrollbar>

    <!-- 右键菜单 -->
    <el-dropdown trigger="click" class="ml-auto">
      <el-button size="small" type="primary" plain>
        操作
        <el-icon class="el-icon--right"><ArrowDown /></el-icon>
      </el-button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item @click="closeOthers(visitedViews.find((v) => v.path === route.path)!)">
            关闭其他
          </el-dropdown-item>
          <el-dropdown-item @click="closeAll"> 关闭所有 </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<style lang="scss" scoped>
.tags-view-container {
  height: 34px;
  width: 100%;
  display: flex;
  align-items: center;
}
:deep(.el-scrollbar__wrap) {
  display: flex;
  align-items: center !important;
}
:deep(.el-scrollbar) {
  display: flex;
  align-items: center !important;
}
.el-tag {
  margin-right: 4px;
  cursor: pointer;

  &:first-of-type {
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: 0;
  }

  &.active {
    background-color: var(--el-color-primary);
    color: white;

    .el-tag__close {
      color: white;
    }
  }
}
</style>
